import { User } from 'oidc-client-ts';
import { parseAccessToken } from './jwtDecode';
import { userManager } from './odUserManager';
import { AccessTokenClaims } from './odUserManager';

/**
 * Tokens accepted by {@link storeTokens}. Loose shape — `oidc-client-ts`'s
 * strict `User` constructor requires `access_token`, `token_type`, and
 * `profile`, all of which we either extract from the JWT or default.
 */
export interface IncomingTokens {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  scope?: string;
  expires_at?: number;
  expires_in?: number;
  profile?: AccessTokenClaims;
  [k: string]: unknown;
}

/**
 * Build a {@link User} from the loose OIDC token-response shape, persist it
 * via {@link userManager.storeUser}, and raise the `userLoaded` event so
 * downstream listeners (`App.routes.tsx`, `temporalRouteTrace.ts`) get
 * notified.
 *
 * Extracted from the old inlined `storeUser()` in `authProvider.ts` so it
 * can be reused from both the password-grant login flows and the new
 * `refreshAccessToken` path.
 */
export async function storeTokens(tokens: IncomingTokens): Promise<void> {
  const jwt = parseAccessToken<AccessTokenClaims>(tokens.access_token);
  const profile: AccessTokenClaims =
    (tokens.profile as AccessTokenClaims | undefined) ??
    (jwt.payload as AccessTokenClaims);

  const now = (Date.now() / 1000) ^ 0;
  const expiresAt =
    tokens.expires_at ?? (tokens.expires_in ? now + tokens.expires_in : undefined);

  const user = new User({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    token_type: tokens.token_type ?? 'Bearer',
    id_token: tokens.id_token,
    scope: tokens.scope,
    expires_at: expiresAt,
    profile: profile as never,
    session_state: null,
  });

  await userManager.storeUser(user);
  // v3 made `events.load` async; awaiting prevents a race with the
  // `addUserLoaded` listeners in `temporalRouteTrace.ts:64` and
  // `App.routes.tsx:186`.
  await userManager.events.load(user);
}
